import { useEffect, useRef, type ReactNode } from 'react';

interface ScrollRevealProps {
  children: ReactNode;
  direction?: 'up' | 'left' | 'right' | 'scale' | 'up-left' | 'up-right' | 'blur';
  className?: string;
  threshold?: number;
  rootMargin?: string;
  stagger?: boolean;
  once?: boolean;
}

export function ScrollReveal({
  children,
  direction = 'up',
  className = '',
  threshold = 0.02,
  rootMargin = '0px 0px -8px 0px',
  stagger = false,
  once = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          if (once) observer.unobserve(el);
        } else if (!once) {
          el.classList.remove('revealed');
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return (
    <div ref={ref} className={`reveal reveal-${direction}${stagger ? ' reveal-stagger' : ''} ${className}`}>
      {children}
    </div>
  );
}